import React from "react";
import Seo from "../components/site/Seo";
import PageHero from "../components/site/PageHero";
import Reveal from "../components/site/Reveal";
import { SectionLabel, ArrowLink, PrimaryButton, PlaceholderNote } from "../components/site/Bits";
import ClosingCTA from "../components/home/ClosingCTA";
import Founders from "../components/home/Founders";
import { ABOUT, SEO, COMPANY, VERTICALS } from "../lib/content";
import { IMAGES } from "../lib/images";

export default function About() {
  return (
    <>
      <Seo title={SEO.about.title} description={SEO.about.description} path="/about" />

      <PageHero
        eyebrow="About LYKIT"
        titleLines={ABOUT.hero}
        tagline={COMPANY.secondaryTagline}
        intro={COMPANY.tagline}
        image={IMAGES.about.hero}
      />

      {/* OUR STORY */}
      <section className="lk-section" data-testid="about-story-section">
        <div className="lk-container-wide">
          <div className="grid gap-y-12 lg:grid-cols-12 lg:gap-x-14">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>Our Story</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-lg mt-7 max-w-[14ch]">{ABOUT.storyHeading}</h2>
              </Reveal>
              <Reveal variant="image" delay={0.16} className="mt-12 hidden lg:block">
                <div className="img-frame img-zoom" style={{ aspectRatio: "4 / 3" }}>
                  <img src={IMAGES.about.story} alt="Production equipment at LYKIT" loading="lazy" />
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <div className="space-y-6">
                {ABOUT.story.map((paragraph, i) => (
                  <Reveal key={paragraph} delay={0.08 + i * 0.06}>
                    <p className="lead measure">{paragraph}</p>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={0.3}>
                <div className="mt-12 grid sm:grid-cols-2">
                  {VERTICALS.map((vertical) => (
                    <div
                      key={vertical.id}
                      className="px-1 py-5 sm:px-6 sm:first:pl-1"
                      style={{ borderTop: "1px solid rgba(23,21,34,0.12)" }}
                    >
                      <span
                        className="font-display text-[11.5px] font-bold"
                        style={{ letterSpacing: "0.16em", color: "rgba(23,21,34,0.4)" }}
                      >
                        {vertical.num}
                      </span>
                      <h3 className="mt-2 font-display text-[16px] font-extrabold tracking-[-0.01em]">
                        {vertical.title}
                      </h3>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* VISION — dark title card */}
      <section className="cinematic relative isolate overflow-hidden" data-testid="about-vision-section">
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img
            src={IMAGES.about.ecosystem}
            alt=""
            className="h-full w-full object-cover"
            style={{ opacity: 0.22 }}
            loading="lazy"
          />
        </div>
        <div
          className="grain absolute inset-0 z-10"
          aria-hidden="true"
          style={{ background: "linear-gradient(180deg, rgba(14,13,20,0.92) 0%, rgba(14,13,20,0.82) 100%)" }}
        />
        <div className="lk-container-wide relative z-20 py-[clamp(40px,4.2vw,70px)]">
          <div className="grid gap-y-10 lg:grid-cols-12 lg:gap-x-14">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel onDark>Our Vision</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <p
                  className="mt-8 font-display text-[clamp(17px,1.8vw,23px)] font-extrabold leading-[1.28] tracking-[-0.02em]"
                  style={{ color: "#FBFAF7" }}
                >
                  {ABOUT.vision}
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.12}>
                <SectionLabel onDark>Our Mission</SectionLabel>
              </Reveal>
              <ol className="mt-8" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {ABOUT.mission.map((item, i) => (
                  <Reveal
                    as="li"
                    key={item}
                    delay={0.12 + i * 0.05}
                    className="flex gap-6 py-5"
                    style={{ borderTop: "1px solid rgba(247,246,242,0.14)" }}
                  >
                    <span
                      className="shrink-0 font-display text-[12px] font-bold"
                      style={{ letterSpacing: "0.14em", color: "rgba(216,183,122,0.9)", paddingTop: 4 }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[15px] leading-[1.66]" style={{ color: "rgba(247,246,242,0.8)" }}>
                      {item}
                    </span>
                  </Reveal>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="lk-section" data-testid="about-values-section">
        <div className="lk-container-wide">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Reveal>
                <SectionLabel>Our Values</SectionLabel>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="display-lg mt-7 max-w-[20ch]">WHAT WE HOLD ON TO.</h2>
              </Reveal>
            </div>
          </div>

          <div className="mt-[clamp(28px,3vw,48px)] grid sm:grid-cols-2 lg:grid-cols-3">
            {ABOUT.values.map((value, i) => (
              <Reveal
                key={value.title}
                delay={i * 0.05}
                className="py-9 sm:px-8 sm:first:pl-0"
                style={{ borderTop: "1px solid rgba(23,21,34,0.10)" }}
              >
                <span aria-hidden="true" className="outline-number block text-[32px]">
                  {value.num}
                </span>
                <h3
                  className="mt-4 font-display text-[clamp(17px,1.4vw,21px)] font-extrabold"
                  style={{ letterSpacing: "0.02em" }}
                >
                  {value.title}
                </h3>
                <p className="mt-3 max-w-[32ch] text-[14px] leading-[1.62] body-muted">{value.text}</p>
              </Reveal>
            ))}
          </div>

          <div className="mt-12">
            <PlaceholderNote>{ABOUT.note}</PlaceholderNote>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-6">
            <PrimaryButton to="/services/events" data-testid="about-explore-services-cta">
              Explore Our Services
            </PrimaryButton>
            <ArrowLink to="/work">See Our Work</ArrowLink>
          </div>
        </div>
      </section>

      <Founders />
      <ClosingCTA />
    </>
  );
}
